/**
 * What the editor is, as data.
 *
 * Modes decide what a click means; tools decide what happens to whatever is
 * already held. Both live here rather than in the panels, because the toolbar,
 * the viewport and the details panel all have to agree about them, and a rule
 * written down three times is a rule that will disagree with itself.
 */

import { SEA_FLOOR, SEA_LEVEL, SHORE_HEIGHT } from '@inverse/common';
import type {
  ItemDefinitionWire,
  NpcDefinitionWire,
  ObjectDefinitionWire,
  ZoneWire,
} from '@inverse/protocol';

// ---------------------------------------------------------------------------
// Modes
// ---------------------------------------------------------------------------

/**
 * What is being edited.
 *
 * A mode is a subject, not an action: the ground, the things standing on it, the
 * pieces a building is made of, the characters that live there. What a click
 * does to that subject is the tool's business, which is why Select is not one of
 * these.
 */
export type EditorMode = 'play' | 'place' | 'build' | 'terrain' | 'spawns';

export const MODES: { mode: EditorMode; label: string; hint: string; key: string }[] = [
  { mode: 'play', label: 'Play', hint: 'Camera and character', key: '1' },
  { mode: 'place', label: 'Place', hint: 'Scenery and props', key: '2' },
  { mode: 'build', label: 'Build', hint: 'Walls, floors and roofs', key: '3' },
  { mode: 'terrain', label: 'Terrain', hint: 'Ground height and material', key: '4' },
  { mode: 'spawns', label: 'Spawns', hint: 'Characters and spawn zones', key: '5' },
];

/**
 * The two halves of Spawns.
 *
 * Stationing a shopkeeper and seeding a wood with rats are different jobs with
 * different questions: one is a person who stands somewhere and talks, the other
 * is a number of things that keep coming back inside an area. They were one mode
 * asking both sets of questions at once.
 */
export type SpawnKind = 'people' | 'enemies';

export const SPAWN_KINDS: { kind: SpawnKind; label: string; hint: string }[] = [
  { kind: 'people', label: 'People', hint: 'Somebody who stands there and can be talked to' },
  {
    kind: 'enemies',
    label: 'Enemies',
    hint: 'Something that comes back, inside an area it keeps to',
  },
];

/** Modes where a selection can be picked up and worked on. */
export const SELECTS: readonly EditorMode[] = ['place', 'build', 'spawns'];

// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------

/**
 * What a click and a drag do, whatever is being edited.
 *
 * The tool is the verb and the mode is the subject. Select is one of these
 * rather than a mode of its own, because picking something out is something you
 * do while building, while placing, and while laying out spawns.
 */
export type TransformTool =
  'select' | 'place' | 'brush' | 'lasso' | 'move' | 'rotate' | 'scale' | 'erase';

/** The tools that take hold of what is selected and change it with a drag. */
export const DRAGGED: TransformTool[] = ['move', 'rotate', 'scale'];

export const TRANSFORM_TOOLS: {
  tool: TransformTool;
  label: string;
  key: string;
  hint: string;
  /** A glyph for the strip, since a row of words is a row nobody reads twice. */
  icon: string;
  /**
   * The tool this one belongs under, for anything that is a way of using
   * another tool rather than a tool of its own.
   *
   * Lasso is a way of selecting, so it lives in the Select tool's options
   * rather than taking a place of its own in the strip.
   */
  under?: TransformTool;
}[] = [
  {
    tool: 'select',
    label: 'Select',
    key: 'Q',
    icon: '▣',
    hint: 'Pick things out, or drag to take in several',
  },
  {
    tool: 'place',
    label: 'Place',
    key: 'P',
    icon: '✚',
    hint: 'Put down whatever the browser is holding',
  },
  {
    tool: 'brush',
    label: 'Brush',
    key: 'B',
    icon: '◐',
    hint: 'Work the ground under the pointer as you go',
  },
  {
    tool: 'lasso',
    label: 'Lasso',
    // Not A: the view is moved with W A S D while the editor holds the world,
    // and a key that both draws a selection and slides the camera is a key
    // nobody can use.
    key: 'L',
    icon: '◌',
    hint: 'Draw over the ground to take in whatever it covers',
    under: 'select',
  },
  // Not W: the view is moved with W A S D while the editor holds the world,
  // and a key that both grabs the selection and slides the camera is a key
  // nobody can use.
  {
    tool: 'move',
    label: 'Move',
    key: 'M',
    icon: '✛',
    hint: 'Drag the selection across the ground',
  },
  {
    tool: 'rotate',
    label: 'Rotate',
    key: 'E',
    icon: '↻',
    hint: 'Drag left or right to turn the selection',
  },
  {
    tool: 'scale',
    label: 'Scale',
    key: 'R',
    icon: '↕',
    hint: 'Drag up or down to resize the selection',
  },
  {
    tool: 'erase',
    label: 'Erase',
    key: 'X',
    icon: '✖',
    hint: 'Click things to take them out of the world',
  },
];

/**
 * What a drag means, given the tool and what the mode is for.
 *
 * Written down in one place because it is the rule that is hardest to keep
 * straight: a drag with Move moves what is held, and only a drag with Select
 * ever draws a box.
 */
export function dragMeans(
  tool: TransformTool,
  mode: EditorMode,
  holding: boolean,
  placing: boolean,
): 'transform' | 'paint' | 'build' | 'zone' | 'marquee' | 'lasso' {
  if (holding && (tool === 'move' || tool === 'rotate' || tool === 'scale')) return 'transform';
  if (tool === 'lasso') return 'lasso';
  // Sculpting works under the pointer only with the Brush. Every other tool in
  // Terrain marks out ground for the Landscape panel to work on, which is what
  // stops a stray click reshaping a hillside somebody was only looking at.
  if (mode === 'terrain') return tool === 'brush' ? 'paint' : 'marquee';
  if (mode === 'build' && placing && tool === 'place') return 'build';
  if (mode === 'spawns' && tool !== 'place') return 'zone';
  return 'marquee';
}

/** What a marquee drag takes in, which depends on what is being worked on. */
export type SelectFilter = 'everything' | 'scenery' | 'building';

export const SELECT_FILTERS: { filter: SelectFilter; label: string }[] = [
  { filter: 'everything', label: 'Everything' },
  { filter: 'scenery', label: 'Scenery' },
  { filter: 'building', label: 'Building' },
];

// ---------------------------------------------------------------------------
// Terrain
// ---------------------------------------------------------------------------

export type TerrainAction =
  | 'clear'
  | 'blocked'
  | 'drain'
  | 'current'
  | 'still'
  | 'settle'
  | 'channel'
  | 'join'
  | 'pond'
  | 'lava'
  | 'sea'
  | 'land'
  | 'raise'
  | 'lower'
  | 'smooth'
  | 'flatten'
  | 'level'
  | 'slope'
  | 'slopeBowl'
  | 'slopeDome'
  | 'strip'
  | 'grass'
  | 'dirt'
  | 'sand'
  | 'rock'
  | 'path'
  | 'snow'
  | 'ash'
  | 'shingle'
  | 'mud'
  | 'natural'
  | 'timber'
  | 'stone'
  | 'cottage'
  | 'hut'
  | 'hall'
  | 'keep'
  | 'tower'
  | 'temple'
  | 'library'
  | 'shop'
  | 'inn'
  | 'workshop'
  | 'barn'
  | 'stable'
  | 'warehouse';

export const TERRAIN_ACTIONS: {
  action: TerrainAction;
  label: string;
  group: string;
  /** What it does to the ground, said under the pointer. */
  hint: string;
}[] = [
  { action: 'raise', label: 'Raise', group: 'Sculpt', hint: 'Pull the ground up under the brush' },
  {
    action: 'lower',
    label: 'Lower',
    group: 'Sculpt',
    hint: 'Push the ground down under the brush',
  },
  {
    action: 'smooth',
    label: 'Smooth',
    group: 'Sculpt',
    hint: 'Ease every corner towards its neighbours, which takes the steps out of a slope',
  },
  {
    action: 'flatten',
    label: 'Flatten',
    group: 'Sculpt',
    hint: 'Bring the whole brush to one height, the average of what is under it',
  },
  {
    action: 'level',
    label: 'Level to here',
    group: 'Sculpt',
    hint: 'Bring the whole brush to the height of the tile it started on',
  },
  {
    action: 'slope',
    label: 'Slope',
    group: 'Slope',
    hint: 'Tilt the ground evenly, the way the drag went',
  },
  {
    action: 'slopeBowl',
    label: 'Dish it out',
    group: 'Slope',
    hint: 'A hollow, deepest in the middle',
  },
  {
    action: 'slopeDome',
    label: 'Round it up',
    group: 'Slope',
    hint: 'A rise, highest in the middle',
  },
  {
    action: 'pond',
    label: 'Fill with water',
    group: 'Water',
    hint: 'Pour water into the box round what is held and let it find its own edges. Nobody wades, so this closes what it covers',
  },
  {
    action: 'lava',
    label: 'Fill with lava',
    group: 'Water',
    hint: 'The same, poured hot. It fills a hollow the way water does and nobody walks into it either',
  },
  {
    action: 'channel',
    label: 'Cut a channel',
    group: 'Water',
    hint: 'A river bed along the longer side of what is held, with banks, and water in it that runs downhill',
  },
  {
    action: 'settle',
    label: 'Settle to one level',
    group: 'Water',
    hint: 'Bring water that was made in pieces up to one surface, so a pond has no seams in it',
  },
  {
    action: 'join',
    label: 'Join what it touches',
    group: 'Water',
    hint: 'Pour at the level of the water already against it, so a new pool is part of the river rather than beside it',
  },
  {
    action: 'current',
    label: 'Make it run',
    group: 'Water',
    hint: 'Set which way the water runs, the way the stroke was drawn, as fast as the strength says',
  },
  {
    action: 'still',
    label: 'Still water',
    group: 'Water',
    hint: 'Stop the water running, so it lies and works at itself the way a pond does',
  },
  {
    action: 'drain',
    label: 'Take the water away',
    group: 'Water',
    hint: 'Empty it and give the ground back',
  },
  {
    action: 'sea',
    label: 'Cut to the sea',
    group: 'Water',
    hint: 'Drop the ground under sea level',
  },
  {
    action: 'land',
    label: 'Fill to land',
    group: 'Water',
    hint: 'Raise the ground above the water and take the water off it',
  },
  {
    action: 'clear',
    label: 'Walkable',
    group: 'Ground',
    hint: 'Open dry ground that was closed by hand. Water is left alone: it is in the way whatever it is flagged',
  },
  {
    action: 'blocked',
    label: 'Solid',
    group: 'Ground',
    hint: 'Close the ground, so nothing can walk on it',
  },
  {
    action: 'strip',
    label: 'Clear scenery',
    group: 'Ground',
    hint: 'Take everything standing on these tiles away',
  },
  { action: 'grass', label: 'Grass', group: 'Paint', hint: 'Paint the ground as grass' },
  { action: 'dirt', label: 'Dirt', group: 'Paint', hint: 'Paint the ground as bare earth' },
  { action: 'sand', label: 'Sand', group: 'Paint', hint: 'Paint the ground as sand' },
  { action: 'rock', label: 'Rock', group: 'Paint', hint: 'Paint the ground as bare stone' },
  { action: 'path', label: 'Path', group: 'Paint', hint: 'Paint a trodden path' },
  { action: 'snow', label: 'Snow', group: 'Paint', hint: 'Paint the ground as lying snow' },
  { action: 'ash', label: 'Ash', group: 'Paint', hint: 'Paint the ground as cold volcanic ash' },
  {
    action: 'shingle',
    label: 'Shingle',
    group: 'Paint',
    hint: 'Paint the ground as a pebble beach',
  },
  { action: 'mud', label: 'Mud', group: 'Paint', hint: 'Paint the ground as wet silt' },
  {
    action: 'natural',
    label: 'Back to natural',
    group: 'Paint',
    hint: 'Take the paint off and let the ground be coloured by where it sits',
  },
  /*
   * Buildings, raised over whatever is marked out.
   *
   * Putting one up a piece at a time is a floor on every tile, a roof on every
   * tile, a wall on every outside edge, and a door and windows found by hand
   * among them: a hundred and fifty clicks for one cottage. These do the same
   * thing in one, and what they lay down is the same pieces, so anything put
   * up this way can be taken apart and changed afterwards.
   */
  {
    action: 'timber',
    label: 'Timber house',
    group: 'Build',
    hint: 'Raise a timber house over the ground held: boards, walls and thatch',
  },
  {
    action: 'stone',
    label: 'Stone house',
    group: 'Build',
    hint: 'Raise a stone house over the ground held: flags, walls and slate',
  },
  {
    action: 'cottage',
    label: 'Cottage',
    group: 'Build',
    hint: 'Raise a cottage: a hearth, a bed and a loom in the corner',
  },
  {
    action: 'hut',
    label: 'Hut',
    group: 'Build',
    hint: 'Raise a hut: a timber frame on bare earth, no windows',
  },
  {
    action: 'hall',
    label: 'Hall',
    group: 'Build',
    hint: 'Raise a hall: stone, and meant to be bigger than a house',
  },
  {
    action: 'keep',
    label: 'Keep',
    group: 'Build',
    hint: 'Raise a keep: three storeys of stone, with a rack on the wall',
  },
  {
    action: 'tower',
    label: 'Tower',
    group: 'Build',
    hint: 'Raise a tower: three storeys of stone, a stair and little else',
  },
  {
    action: 'temple',
    label: 'Temple',
    group: 'Build',
    hint: 'Raise a temple: stone, with an altar at the far end and benches',
  },
  {
    action: 'library',
    label: 'Library',
    group: 'Build',
    hint: 'Raise a library: shelves against the far wall, a table to read at',
  },
  {
    action: 'shop',
    label: 'Shop',
    group: 'Build',
    hint: 'Raise a shop: a counter facing the door, with stock behind it',
  },
  {
    action: 'inn',
    label: 'Inn',
    group: 'Build',
    hint: 'Raise an inn: two storeys, a counter, a hearth and tables',
  },
  {
    action: 'workshop',
    label: 'Workshop',
    group: 'Build',
    hint: 'Raise a workshop: a furnace and an anvil under a slate roof',
  },
  {
    action: 'barn',
    label: 'Barn',
    group: 'Build',
    hint: 'Raise a barn: a stone floor under a timber frame and thatch',
  },
  {
    action: 'stable',
    label: 'Stable',
    group: 'Build',
    hint: 'Raise a stable: thatch over a stone floor, bales at the back',
  },
  {
    action: 'warehouse',
    label: 'Warehouse',
    group: 'Build',
    hint: 'Raise a warehouse: stone, no windows, stacked to the walls',
  },
];

export const TERRAIN_GROUPS = ['Sculpt', 'Slope', 'Water', 'Ground', 'Paint', 'Build'];

/**
 * Which kind of building a stroke raises, or nothing for the ones that do not.
 *
 * A building is marked out the same way a lake is, so it belongs with the
 * other things done to an area, and it is told apart here rather than by a
 * second tool with its own way of selecting ground.
 */
export function buildingKind(action: TerrainAction): string | null {
  switch (action) {
    case 'timber':
    case 'stone':
    case 'cottage':
    case 'hut':
    case 'hall':
    case 'keep':
    case 'tower':
    case 'temple':
    case 'library':
    case 'shop':
    case 'inn':
    case 'workshop':
    case 'barn':
    case 'stable':
    case 'warehouse':
      return action;
    default:
      return null;
  }
}

/**
 * What a terrain stroke asks the server for.
 *
 * Kept here rather than in the panel because the same stroke can come from a
 * click with a brush on it or from a dragged out area, and both have to mean
 * exactly the same thing.
 */
export function terrainCommand(
  action: TerrainAction,
  strength = 0.25,
  round = false,
  /**
   * Which way the stroke went, and how high a ramp should end up.
   *
   * A ramp faces the way it was drawn, which is the one thing about a ramp that
   * nobody should have to say twice. A top given is the height its high side
   * ends up at; without one it rises by the strength.
   */
  ramp?: { x: number; z: number; top?: number },
): {
  numbers: Record<string, number>;
  text: Record<string, string>;
} {
  const brush: Record<string, string> = round ? { brush: 'round' } : {};

  switch (action) {
    case 'raise':
      return { numbers: { by: strength }, text: { ...brush } };
    case 'lower':
      return { numbers: { by: -strength }, text: { ...brush } };
    // How much of the way there one stroke goes. At full strength Flatten
    // levels what is marked in one pass, which is what somebody dragging the
    // slider to the end is asking for; anything less eases towards it.
    case 'smooth':
      return { numbers: { strength }, text: { shape: 'smooth' } };
    case 'flatten':
      return { numbers: { strength }, text: { shape: 'flatten' } };
    case 'level':
      return { numbers: {}, text: { shape: 'level' } };
    // A ramp across the marked ground, running the way it was drawn.
    case 'slope':
      return {
        numbers: {
          by: strength * 6,
          alongX: ramp?.x ?? 1,
          alongZ: ramp?.z ?? 0,
          ...(ramp?.top !== undefined ? { top: ramp.top } : {}),
        },
        text: { shape: 'slope' },
      };
    // A dish and a mound. The strength is how deep or how high.
    case 'slopeBowl':
    case 'slopeDome':
      return { numbers: { by: strength * 6 }, text: { shape: action } };
    case 'strip':
      return { numbers: {}, text: { clear: 'objects' } };
    // What the ground is made of, which is drawing rather than collision.
    case 'grass':
    case 'dirt':
    case 'sand':
    case 'rock':
    case 'path':
    case 'snow':
    case 'ash':
    case 'shingle':
    case 'mud':
      return { numbers: {}, text: { ground: action, ...brush } };
    // A building is not a stroke on the ground: what it wants is sent with the
    // area by whatever is raising it, not worked out here.
    case 'timber':
    case 'stone':
    case 'cottage':
    case 'hut':
    case 'hall':
    case 'keep':
    case 'tower':
    case 'temple':
    case 'library':
    case 'shop':
    case 'inn':
    case 'workshop':
    case 'barn':
    case 'stable':
    case 'warehouse':
      return { numbers: {}, text: {} };
    case 'natural':
      return { numbers: {}, text: { ground: 'auto', ...brush } };
    // Water fills to the rim of whatever it is poured into, so a pond sits at the
    // height of the ground around it wherever that ground happens to be. The bed
    // is dug out under it in the same stroke.
    case 'pond':
      return {
        numbers: { depth: Math.max(0.25, strength * 3) },
        text: { water: 'fill', clear: 'objects', ...brush },
      };
    // Lava is water with the ground under it painted lava, which is how the two
    // are told apart everywhere: one surface, one set of rules about walking
    // into it, and a different answer about what it looks like.
    case 'lava':
      return {
        numbers: { depth: Math.max(0.25, strength * 3) },
        text: { water: 'fill', ground: 'lava', clear: 'objects', ...brush },
      };
    // A channel is dug and filled in one stroke, because the two have to agree:
    // a bed cut to one number and water poured to another is a dry trench with
    // a puddle in the bottom, which is the single most common way a river drawn
    // by hand comes out wrong.
    case 'channel':
      return {
        numbers: { depth: Math.max(0.4, strength * 2.5) },
        text: { water: 'channel', clear: 'objects', ...brush },
      };
    case 'settle':
      return { numbers: {}, text: { water: 'settle' } };
    // Pouring at the level of whatever is already against it, so the two are
    // one body. Without this, matching a river means reading its level off the
    // ground pane and typing it in, and being out by a hundredth leaves a seam.
    case 'join':
      return {
        numbers: { depth: Math.max(0.25, strength * 3) },
        text: { water: 'join', clear: 'objects', ...brush },
      };
    // Which way it runs, taken from the way the stroke was drawn, the same as a
    // ramp. A river that has been widened or redrawn by hand needs this: the
    // shape of a body says how fast water could run through it and never which
    // end the sea is at.
    case 'current':
      return {
        numbers: {
          // Its own number rather than the one every other stroke moves ground
          // with: a stroke that sent a strength as `by` would raise the ground
          // it was meant to set running.
          pace: strength,
          alongX: ramp?.x ?? 1,
          alongZ: ramp?.z ?? 0,
        },
        text: { water: 'current' },
      };
    case 'still':
      return { numbers: {}, text: { water: 'still', ...brush } };
    case 'drain':
      return { numbers: {}, text: { water: 'drain', ...brush } };
    // The coast: cutting drops the ground to the sea floor and floods it to sea
    // level, filling lifts it just clear of the water and dries it out.
    case 'sea':
      return {
        numbers: { level: SEA_FLOOR, surface: SEA_LEVEL, depth: 0.25 },
        text: { water: 'fill', clear: 'objects' },
      };
    case 'land':
      return { numbers: { level: SHORE_HEIGHT }, text: { water: 'drain', flags: 'clear' } };
    default:
      return { numbers: {}, text: { flags: action, ...brush } };
  }
}

// ---------------------------------------------------------------------------
// Areas and sides
// ---------------------------------------------------------------------------

export interface TileArea {
  tx: number;
  tz: number;
  width: number;
  height: number;
}

export function areaBetween(
  from: { tx: number; tz: number },
  to: { tx: number; tz: number },
): TileArea {
  return {
    tx: Math.min(from.tx, to.tx),
    tz: Math.min(from.tz, to.tz),
    width: Math.abs(to.tx - from.tx) + 1,
    height: Math.abs(to.tz - from.tz) + 1,
  };
}

/** The square a brush of a given size covers around a tile. */
export function brushArea(tx: number, tz: number, size: number): TileArea {
  const reach = Math.floor((size - 1) / 2);
  return { tx: tx - reach, tz: tz - reach, width: size, height: size };
}

export function areaHolds(area: TileArea, tx: number, tz: number): boolean {
  return tx >= area.tx && tz >= area.tz && tx < area.tx + area.width && tz < area.tz + area.height;
}

/** Every side of a tile, nearest to the point first. */
function sidesByNearness(fx: number, fz: number): number[] {
  const distance = [1 - fz, 1 - fx, fz, fx];
  return [0, 1, 2, 3].sort((a, b) => (distance[a] ?? 1) - (distance[b] ?? 1));
}

/**
 * Which side of a tile a point is nearest, as quarter turns.
 *
 * Nought is the south edge, and it goes round anticlockwise: south, east,
 * north, west. That is not a choice anybody would make from scratch. It is
 * what every wall in the world was built with, from back when the camera
 * rested facing south and the engine called that direction north. Renaming the
 * turns to start at north would move every wall already standing onto the
 * opposite edge of its tile, so the turns keep their meaning and the words
 * were corrected instead.
 */
export function nearestSide(fx: number, fz: number): number {
  return sidesByNearness(fx, fz)[0] ?? 0;
}

/** The tile on the other side of one of a tile's edges. */
export function tileBeyond(tx: number, tz: number, side: number): { tx: number; tz: number } {
  switch (side & 3) {
    case 0:
      return { tx, tz: tz + 1 };
    case 1:
      return { tx: tx + 1, tz };
    case 2:
      return { tx, tz: tz - 1 };
    default:
      return { tx: tx - 1, tz };
  }
}

/**
 * Which side of a tile a wall should take, given where the floors are.
 *
 * A wall put on a floored tile takes an outside edge, because that is what a room
 * is: floor inside, wall around it. A wall put beside a floor faces it, so a run
 * laid just outside a floor hugs it rather than turning its back. With no floor
 * anywhere near, it is simply the side the pointer was nearest.
 */
export function wallSide(
  tx: number,
  tz: number,
  fx: number,
  fz: number,
  floored: (tx: number, tz: number) => boolean,
): number {
  /*
   * The middle of a tile means across it.
   *
   * A fence that runs at an angle is built corner to corner, and the way to ask
   * for one is to point at the middle of the tile rather than at one of its
   * sides. Which of the two diagonals is whichever the pointer is nearer: the
   * one it is standing on.
   *
   * Only on bare ground. Inside a building the sides are what somebody is
   * pointing at, and a wall across the corner of a room is almost never what
   * was wanted.
   */
  if (!floored(tx, tz)) {
    const offMiddle = Math.max(Math.abs(fx - 0.5), Math.abs(fz - 0.5));
    if (offMiddle < 0.22) {
      // The line fx equals fz runs from one corner to the opposite one; the
      // other diagonal is the line fx equals one minus fz.
      const rising = Math.abs(fx - fz) < Math.abs(fx + fz - 1);
      return rising ? 4 : 5;
    }
  }

  const nearest = sidesByNearness(fx, fz);

  if (floored(tx, tz)) {
    const outside = nearest.find((side) => {
      const beyond = tileBeyond(tx, tz, side);
      return !floored(beyond.tx, beyond.tz);
    });
    return outside ?? nearest[0] ?? 0;
  }

  const facing = nearest.find((side) => {
    const beyond = tileBeyond(tx, tz, side);
    return floored(beyond.tx, beyond.tz);
  });

  return facing ?? nearest[0] ?? 0;
}

// ---------------------------------------------------------------------------
// The content browser
// ---------------------------------------------------------------------------

export type Category =
  'scenery' | 'resources' | 'crafting' | 'transport' | 'utilities' | 'building';

export const CATEGORIES: { category: Category; label: string }[] = [
  { category: 'scenery', label: 'Scenery' },
  { category: 'resources', label: 'Resources' },
  { category: 'crafting', label: 'Crafting' },
  { category: 'transport', label: 'Transportation' },
  { category: 'utilities', label: 'Utilities' },
  { category: 'building', label: 'Building' },
];

/**
 * What a building part is, so the browser can sort them.
 *
 * Thirty pieces in one list is a list nobody reads to the end of. Which kind a
 * piece is comes from its name, which is where it already is: everything laid
 * flat is called a floor or a board, everything upright is a wall, and so on.
 * Anything the rule cannot place lands in Other, where it can still be found.
 */
export const BUILDING_GROUPS = [
  'floors',
  'walls',
  'fences',
  'roofs',
  'doors',
  'stairs',
  'supports',
  'other',
] as const;

const PART_RULES: { group: string; match: RegExp }[] = [
  { group: 'floors', match: /floor|board|paving|tile/i },
  { group: 'roofs', match: /roof|thatch|gable|eave/i },
  { group: 'doors', match: /door|gate|window|arch|hatch/i },
  { group: 'stairs', match: /stair|ladder|step|way (up|out)|cave mouth/i },
  { group: 'fences', match: /fence|rail|hedge|palis/i },
  { group: 'supports', match: /support|post|pillar|beam|strut/i },
  { group: 'walls', match: /wall|panel|partition/i },
];

export function partGroupOf(name: string): string {
  return PART_RULES.find((rule) => rule.match.test(name))?.group ?? 'other';
}

/**
 * What each drawer is, said in the words the actions are written in.
 *
 * A thing goes where its verbs say it goes. A fishing spot says Net, Lure, Cage
 * and Harpoon, none of which look like gathering to a list that only knew about
 * chopping and mining, so it spent its life in Utilities beside the notice
 * board. An anvil and a cooking fire are places where things are made, which is
 * a different job from a well or a strongbox. And everything that carries
 * somebody somewhere else belongs together, whether it does it by a stone, a
 * rope or a hole in a hillside.
 */
const GATHERING = /^(chop|mine|fish|cut|dig|pick|net|lure|cage|harpoon|gather|forage)/i;
const MAKING = /^(cook|smelt|smith|craft|spin|weave|brew|tan|fletch|carve)/i;
const TRAVELLING = /^(touch|enter|leave|climb|board|ride|sail|board-)/i;

export function categoryOf(object: ObjectDefinitionWire): string {
  // Every piece under one heading, because each kind of piece is now a single
  // entry with its options behind it: eight headings with one tile under each
  // is a list of headings rather than a browser.
  if (object.piece) return 'building';
  if (object.actions.some((action) => GATHERING.test(action))) return 'resources';
  if (object.actions.some((action) => MAKING.test(action))) return 'crafting';
  if (object.actions.some((action) => TRAVELLING.test(action))) return 'transport';
  return object.actions.length > 0 ? 'utilities' : 'scenery';
}

/** Something the browser can put in the world. */
export interface Placeable {
  kind: 'object' | 'character' | 'piece' | 'loot';
  id: number;
  name: string;
  category: string;
  detail: string;
  /** Characters that fight, which are the ones that get a zone of their own. */
  fights?: boolean;
  /** Whose shape a made kind of scenery borrows, so its picture is that one. */
  looksLike?: number;
  /** The kind of thing this is one of, and which one, for anything with tiers. */
  family?: string;
  tier?: string;
  /** Whether it is part of a building rather than a thing standing on its own. */
  piece?: boolean;
  /** The other half of a door, for anything that swings. Nought for the rest. */
  opensTo?: number;
}

export function placeableObjects(objects: readonly ObjectDefinitionWire[]): Placeable[] {
  const shut = shutHalves(objects);

  return (
    objects
      // A door is one thing with two states, so the browser lists it once. Both
      // halves used to sit in it under names that said which state they were,
      // which invited somebody to build the open half of a door beside the shut
      // half of another one.
      .filter((object) => !(object.opensTo > 0 && !shut.has(object.id)))
      .map((object) => ({
        kind: 'object',
        id: object.id,
        name: object.name,
        category: categoryOf(object),
        detail:
          object.opensTo > 0
            ? 'opens'
            : object.actions.length > 0
              ? object.actions.join(', ')
              : object.piece
                ? 'piece'
                : 'scenery',
        looksLike: object.looksLike,
        // A building piece belongs to the kind of piece it is, whatever it is
        // called: flooring is flooring, and somebody laying a floor wants the
        // choice of boards or flags rather than two entries in a list of thirty.
        // Anything else keeps whatever family it was written with.
        family: object.piece ? partGroupOf(object.name) : object.family,
        // The name it is known by inside its own kind, so the list behind the
        // chevron reads as the choice it is.
        tier: object.piece ? object.name : object.tier,
        piece: object.piece,
        opensTo: object.opensTo,
      }))
  );
}

/**
 * Which half of each pair of doors is the shut one.
 *
 * The one that stops somebody walking through it. Failing that, whichever has
 * the lower id, so a pair that blocks neither way still has one of them listed
 * rather than both or neither.
 */
export function shutHalves(objects: readonly ObjectDefinitionWire[]): Set<number> {
  const byId = new Map(objects.map((object) => [object.id, object]));
  const held = new Set<number>();

  for (const object of objects) {
    if (object.opensTo <= 0) continue;

    const other = byId.get(object.opensTo);
    if (!other || other.opensTo !== object.id) {
      // Half a pair, which is a mistake in the data rather than a door. Listed
      // anyway: hiding it would hide the mistake.
      held.add(object.id);
      continue;
    }

    if (object.barrier !== 'open') held.add(object.id);
    else if (other.barrier === 'open' && object.id < other.id) held.add(object.id);
  }

  return held;
}

/** The other half of a door, for placing it the way it should rest. */
export function otherHalf(
  objects: readonly ObjectDefinitionWire[],
  id: number,
): ObjectDefinitionWire | undefined {
  const held = objects.find((object) => object.id === id);
  return held && held.opensTo > 0 ? objects.find((one) => one.id === held.opensTo) : undefined;
}

/** How the spawn palette groups characters: by what they are, bosses last. */
export function characterCategories(characters: readonly NpcDefinitionWire[]): string[] {
  const order = ['townsfolk', 'beasts', 'undead', 'bosses', 'other'];
  const held = new Set(characters.map((character) => character.category || 'other'));
  return order.filter((category) => held.has(category));
}

export function placeableCharacters(characters: readonly NpcDefinitionWire[]): Placeable[] {
  return characters.map((character) => ({
    kind: 'character',
    id: character.id,
    name: character.name,
    category: character.category || 'other',
    detail: character.combatLevel > 0 ? `level ${character.combatLevel}` : 'talks',
    fights: character.combatLevel > 0,
  }));
}

/**
 * Items, as things to leave lying in the world.
 *
 * Dropped loot is a thing in transit. This is the other kind: a knife on a
 * table, a key on a shelf, a hoard in a cave. Grouped by what the item is, so
 * the browser reads the same way it does for scenery.
 */
export function placeableLoot(items: readonly ItemDefinitionWire[]): Placeable[] {
  return items.map((item) => ({
    kind: 'loot',
    id: item.id,
    name: item.name,
    // One heading rather than one per kind of item: this is a list of things to
    // leave lying about, and splitting it by whether the thing is a sword or a
    // fish is splitting it by the wrong question.
    category: 'loot',
    detail: item.kind || 'material',
  }));
}

/** The heading a group is written under, for the few that are not just titled. */
const GROUP_LABELS: Readonly<Record<string, string>> = {
  loot: 'Loot',
  stairs: 'Ways up',
  doors: 'Doors and windows',
  building: 'Building',
  transport: 'Transportation',
};

/**
 * What one kind of building piece is called as a single entry.
 *
 * The browser shows one tile per kind with the options behind it, so the tile
 * has to be named for the kind rather than for whichever option was last
 * chosen: laying a floor is choosing Flooring and then choosing boards, not
 * hunting for the word Board in a list.
 */
export const PIECE_LABELS: Readonly<Record<string, string>> = {
  floors: 'Flooring',
  walls: 'Walls',
  fences: 'Fencing',
  roofs: 'Roofing',
  doors: 'Doors and windows',
  stairs: 'Ways up',
  supports: 'Supports',
  other: 'Other pieces',
};

export function labelFor(category: string): string {
  const named = GROUP_LABELS[category];
  if (named) return named;

  return category.length > 0 ? (category[0]?.toUpperCase() ?? '') + category.slice(1) : 'Other';
}

/**
 * What the server knows about the selected spawn.
 *
 * Loose numbers and text rather than a shape of its own, so the panel reads what
 * it needs and ignores the rest. The server can start sending something new
 * without the client having to be taught about it first.
 */
export interface Facts {
  kind: string;
  numbers: Record<string, number>;
  text: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

export interface ObjectPick {
  kind: 'object';
  tx: number;
  tz: number;
  objectId: number;
  rotation: number;
  /** The size it is drawn at, when that is not the size it was modelled at. */
  scale?: number;
  /** The height it was built to, for anything standing out over water. */
  lift?: number;
  /** A shape of its own, borrowed from something else. */
  looks?: number;
  /**
   * What this one can be worked for, when it differs from its kind.
   *
   * Absent means it is worked however its kind is worked, which is where every
   * spot starts.
   */
  ways?: { action: string }[];
}

export interface CharacterPick {
  kind: 'character';
  /** Zero when it was chosen from a list rather than clicked on in the world. */
  entityId: number;
  /** Where it is stationed, which is how one nobody is standing near is asked about. */
  tx?: number;
  tz?: number;
}

export interface ZonePick {
  kind: 'zone';
  zoneId: number;
}

export interface TilePick {
  kind: 'tile';
  tx: number;
  tz: number;
}

export interface AreaPick {
  kind: 'area';
  area: TileArea;
}

/**
 * A shape of ground rather than a rectangle of it.
 *
 * What a lasso leaves behind: the tiles inside the loop that was drawn, which is
 * rarely a rectangle and is the whole reason for drawing one.
 */
export interface TilesPick {
  kind: 'tiles';
  tiles: { tx: number; tz: number }[];
}

export type Pick = ObjectPick | CharacterPick | ZonePick | TilePick | AreaPick | TilesPick;

/**
 * Every tile a selection covers.
 *
 * The Landscape panel works on whatever is held, and what is held may be a
 * rectangle, a lassoed shape, a single tile, or the ground under a row of
 * things that were picked out one at a time.
 */
export function tilesOf(selection: Selection): { tx: number; tz: number }[] {
  const found = new Map<string, { tx: number; tz: number }>();

  const add = (tx: number, tz: number): void => {
    found.set(`${tx},${tz}`, { tx, tz });
  };

  for (const pick of selection.picks) {
    if (pick.kind === 'tiles') {
      for (const tile of pick.tiles) add(tile.tx, tile.tz);
    } else if (pick.kind === 'area') {
      for (let dz = 0; dz < pick.area.height; dz++) {
        for (let dx = 0; dx < pick.area.width; dx++) add(pick.area.tx + dx, pick.area.tz + dz);
      }
    } else if (pick.kind === 'object' || pick.kind === 'tile') {
      add(pick.tx, pick.tz);
    }
  }

  return [...found.values()];
}

/**
 * What the editor has hold of.
 *
 * A list rather than one thing, because a tile carries more than one thing and
 * because a building is many things at once. `active` is the one the details
 * panel is showing, which is what the tabs across the top of it choose between.
 */
export interface Selection {
  picks: Pick[];
  active: number;
  /**
   * Whether this list was gathered on purpose.
   *
   * A click puts up everything under the pointer so it can be chosen between,
   * which is not the same as asking for all of it. A box drawn round several
   * things, or Ctrl held while clicking, is. Only a gathered list is what the
   * transform tools act on; otherwise they act on the one being shown.
   */
  gathered: boolean;
}

export const NOTHING_SELECTED: Selection = { picks: [], active: 0, gathered: false };

export function activePick(selection: Selection): Pick | undefined {
  return selection.picks[selection.active];
}

export function samePick(a: Pick, b: Pick): boolean {
  if (a.kind !== b.kind) return false;

  if (a.kind === 'object' && b.kind === 'object') {
    return a.tx === b.tx && a.tz === b.tz && a.objectId === b.objectId && a.rotation === b.rotation;
  }
  if (a.kind === 'character' && b.kind === 'character') {
    if (a.entityId !== 0 && b.entityId !== 0) return a.entityId === b.entityId;
    return a.tx === b.tx && a.tz === b.tz;
  }
  if (a.kind === 'zone' && b.kind === 'zone') return a.zoneId === b.zoneId;
  if (a.kind === 'tile' && b.kind === 'tile') return a.tx === b.tx && a.tz === b.tz;
  return false;
}

export function selectionOf(picks: Pick[], gathered = false): Selection {
  return { picks, active: 0, gathered };
}

/** How a new set of picks meets what is already held. */
export type HowSelected = 'replace' | 'add' | 'remove';

/** Which modifier is down, if any, when something is picked. */
export function howFrom(adding: boolean, removing: boolean): HowSelected {
  if (removing) return 'remove';
  return adding ? 'add' : 'replace';
}

/**
 * Puts a set of picks together with what is already held.
 *
 * Ground and things are kept apart. Adding ground to ground makes one shape out
 * of the two, however each of them was drawn, because two overlapping rectangles
 * are not two selections; adding a thing to a list of things makes a longer
 * list. Taking away does the same in reverse, so a shape can be drawn and then
 * bitten into with Shift held.
 */
export function withPicks(
  selection: Selection,
  incoming: readonly Pick[],
  how: HowSelected,
): Selection {
  if (how === 'replace') return selectionOf([...incoming], incoming.length > 1);

  const isGround = (pick: Pick): boolean =>
    pick.kind === 'tile' || pick.kind === 'area' || pick.kind === 'tiles';

  const key = (tile: { tx: number; tz: number }): string => `${tile.tx},${tile.tz}`;

  const groundOf = (picks: readonly Pick[]): Map<string, { tx: number; tz: number }> => {
    const found = new Map<string, { tx: number; tz: number }>();
    for (const tile of tilesOf({ picks: picks.filter(isGround), active: 0, gathered: true })) {
      found.set(key(tile), tile);
    }
    return found;
  };

  const heldGround = groundOf(selection.picks);
  const newGround = groundOf(incoming);

  if (how === 'add') {
    for (const [at, tile] of newGround) heldGround.set(at, tile);
  } else {
    for (const at of newGround.keys()) heldGround.delete(at);
  }

  const heldThings = selection.picks.filter((pick) => !isGround(pick));
  const newThings = incoming.filter((pick) => !isGround(pick));

  const things =
    how === 'add'
      ? [
          ...heldThings,
          ...newThings.filter((pick) => !heldThings.some((was) => samePick(was, pick))),
        ]
      : heldThings.filter((pick) => !newThings.some((going) => samePick(going, pick)));

  const picks: Pick[] = [...things];
  if (heldGround.size > 0) picks.push({ kind: 'tiles', tiles: [...heldGround.values()] });

  return { picks, active: 0, gathered: true };
}

/**
 * What the tools act on.
 *
 * Everything, when the list was gathered on purpose. Otherwise only the one the
 * panel is showing, because the rest are alternatives rather than company.
 */
export function objectPicks(selection: Selection): ObjectPick[] {
  if (selection.gathered) {
    return selection.picks.filter((pick): pick is ObjectPick => pick.kind === 'object');
  }

  const showing = activePick(selection);
  return showing?.kind === 'object' ? [showing] : [];
}

/** What the tabs across the details panel say. */
export function tabNameOf(
  pick: Pick,
  objectName: (id: number) => string,
  characterName: (id: number) => string,
  zoneName: (id: number) => string,
): string {
  switch (pick.kind) {
    case 'object':
      return objectName(pick.objectId);
    case 'character':
      return characterName(pick.entityId);
    case 'zone':
      return zoneName(pick.zoneId);
    case 'area':
      return `${pick.area.width} by ${pick.area.height}`;
    case 'tiles':
      return `${pick.tiles.length} tiles`;
    case 'tile':
      return 'Ground';
  }
}

// ---------------------------------------------------------------------------
// Zones
// ---------------------------------------------------------------------------

export function zoneHolds(zone: ZoneWire, tx: number, tz: number): boolean {
  return zone.tiles.some((tile) => tile.tx === tx && tile.tz === tz);
}

export function zonesOn(zones: readonly ZoneWire[], tx: number, tz: number): ZoneWire[] {
  return zones.filter((zone) => zoneHolds(zone, tx, tz));
}

/** Ticks are the world's clock; seconds are what a person setting a timer thinks in. */
export function secondsOf(ticks: number): string {
  return (ticks * 0.6).toFixed(1);
}

export function ticksOf(seconds: number): number {
  return Math.max(1, Math.round(seconds / 0.6));
}

// ---------------------------------------------------------------------------
// What the editor was doing last time
// ---------------------------------------------------------------------------

/**
 * The settings worth remembering between sessions.
 *
 * Not what is selected or where the view is, which belong to the moment, but
 * the way somebody has set the tools up: the brush they work with, how hard it
 * presses, whether scenery is scattered. Coming back to a brush of one and a
 * square edge every time is a small tax paid over and over.
 */
export interface EditorSettings {
  mode: EditorMode;
  tool: TransformTool;
  terrain: TerrainAction;
  brush: number;
  strength: number;
  roundBrush: boolean;
  /** The height a ramp's high side ends up at, or null to rise by the strength. */
  slopeTop: number | null;
  vary: boolean;
  density: number;
  filter: SelectFilter;
  zoneMax: number;
  zoneMin: number;
  zoneSpan: number;
}

export const DEFAULT_EDITOR_SETTINGS: EditorSettings = {
  mode: 'place',
  tool: 'select',
  terrain: 'raise',
  brush: 1,
  strength: 0.25,
  roundBrush: true,
  slopeTop: null,
  vary: true,
  density: 1,
  filter: 'everything',
  zoneMax: 3,
  zoneMin: 25,
  zoneSpan: 25,
};

const SETTINGS_KEY = 'inverse.editor';

export function loadEditorSettings(): EditorSettings {
  try {
    const held = window.localStorage.getItem(SETTINGS_KEY);
    if (!held) return DEFAULT_EDITOR_SETTINGS;

    // Anything missing or nonsense falls back, so a settings file from an older
    // build cannot leave the editor in a state it has no buttons for.
    const parsed = JSON.parse(held) as Partial<EditorSettings>;
    return {
      ...DEFAULT_EDITOR_SETTINGS,
      ...parsed,
      mode: MODES.some((entry) => entry.mode === parsed.mode)
        ? (parsed.mode as EditorMode)
        : DEFAULT_EDITOR_SETTINGS.mode,
      tool: TRANSFORM_TOOLS.some((entry) => entry.tool === parsed.tool)
        ? (parsed.tool as TransformTool)
        : DEFAULT_EDITOR_SETTINGS.tool,
      terrain: TERRAIN_ACTIONS.some((entry) => entry.action === parsed.terrain)
        ? (parsed.terrain as TerrainAction)
        : DEFAULT_EDITOR_SETTINGS.terrain,
    };
  } catch {
    return DEFAULT_EDITOR_SETTINGS;
  }
}

export function saveEditorSettings(settings: EditorSettings): void {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // A browser that will not keep settings is not a reason to stop editing.
  }
}

// ---------------------------------------------------------------------------
// Pieces: a selection kept to be put down again
// ---------------------------------------------------------------------------

/**
 * Something built once and kept to be built again.
 *
 * A hut, a length of fence with a gate in it, a camp: the shapes a world is
 * actually made of are groups rather than single things, and rebuilding one
 * wall at a time every time is how a world stops being built.
 *
 * Kept in the browser rather than on the server: these are somebody's own way
 * of working, like a brush size, not part of the world everybody shares.
 */
export interface Piece {
  name: string;
  parts: { id: number; dx: number; dz: number; rotation: number; scale?: number }[];
}

const PIECES_KEY = 'inverse.editor.pieces';

export function loadPieces(): Piece[] {
  try {
    const held = window.localStorage.getItem(PIECES_KEY);
    if (!held) return [];

    const parsed = JSON.parse(held) as Piece[];
    return Array.isArray(parsed)
      ? parsed.filter((piece) => typeof piece?.name === 'string' && Array.isArray(piece.parts))
      : [];
  } catch {
    return [];
  }
}

export function savePieces(pieces: readonly Piece[]): void {
  try {
    window.localStorage.setItem(PIECES_KEY, JSON.stringify(pieces.slice(0, 60)));
  } catch {
    // Nothing kept is better than nothing working.
  }
}

/** Turns what is held into a piece, measured from its own corner. */
export function pieceFrom(name: string, picks: readonly ObjectPick[]): Piece {
  const cornerX = Math.min(...picks.map((pick) => pick.tx));
  const cornerZ = Math.min(...picks.map((pick) => pick.tz));

  return {
    name,
    parts: picks.map((pick) => ({
      id: pick.objectId,
      dx: pick.tx - cornerX,
      dz: pick.tz - cornerZ,
      rotation: pick.rotation,
      ...(pick.scale !== undefined && pick.scale !== 1 ? { scale: pick.scale } : {}),
    })),
  };
}

/** Whether an action reshapes a whole area at once rather than a tile at a time. */
export function shapesWholeArea(action: TerrainAction): boolean {
  return (
    action === 'smooth' ||
    action === 'flatten' ||
    action === 'level' ||
    action === 'slope' ||
    action === 'slopeBowl' ||
    action === 'slopeDome'
  );
}

/**
 * Strokes that have to be sent as one piece of ground rather than row by row.
 *
 * Scattered tiles go as rows, which turns two hundred commands into twenty and
 * is right for anything decided a tile at a time: paint, flags, raising.
 *
 * Water is not decided a tile at a time. A pour works out the level the ground
 * it was given can hold and digs its bed to suit, so nine rows of a pond are
 * nine ponds: a nine tile square drawn on a gentle slope filled to 9.13, then
 * 9.28, 9.44, 9.56, 9.69 and finally 10.47, each pour joining the last and
 * lifting the whole body again, and what came out was a sheet of water lying
 * across a field at an angle. A channel is worse: it runs along the longer side
 * of what it is given, so every row of it would be its own river running the
 * wrong way.
 *
 * A stroke that goes whole works on the box round what is held, which is what
 * shaping has always done. The brush has a round setting for a pond that should
 * not be square.
 */
export function wantsWholeArea(action: TerrainAction): boolean {
  return (
    shapesWholeArea(action) ||
    action === 'pond' ||
    action === 'lava' ||
    action === 'channel' ||
    action === 'join' ||
    action === 'settle' ||
    // Which way the stroke went is a fact about the whole stroke.
    action === 'current'
  );
}
